<template>
  <div>
    <!-- Trigger Button -->
    <slot name="trigger">
      <v-btn
        :color="buttonColor"
        :outlined="buttonOutlined"
        :small="buttonSmall"
        @click="dialogVisible = true"
      >
        <v-icon left>mdi-heart-pulse</v-icon>
        {{ buttonText }}
      </v-btn>
    </slot>

    <!-- Dialog -->
    <v-dialog
      v-model="dialogVisible"
      max-width="900"
      transition="dialog-bottom-transition"
    >
      <div ref="healthCard">
        <v-card class="health-score-dialog elevation-4" :class="dialogBgClass">
          <!-- Header -->
          <v-card-title class="px-6 py-4" :style="headerStyle">
            <v-row align="center" no-gutters>
              <!-- Title Text -->
              <v-col cols="auto">
                <v-avatar
                  :color="overallAvatarColor"
                  size="56"
                  class="elevation-2"
                >
                  <v-icon dark size="32">{{ overallGradeIcon }}</v-icon>
                </v-avatar>
              </v-col>

              <v-col class="ml-4">
                <div class="text-h6 font-weight-bold d-flex align-center" :style="headerTextStyle">
                  <v-chip
                    v-if="customerSegment"
                    small
                    class="mr-2"
                    :color="segmentBadgeColor"
                    label
                  >
                    <v-icon left small>{{ segmentBadgeIcon }}</v-icon>
                    {{ customerSegment.segment_label }}
                  </v-chip>
                  Εικόνα Πελάτη
                </div>
                <div class="text-caption" :style="{ opacity: headerTextOpacity }">
                  ID: {{ customer_id }} • {{ customerName }}
                </div>
                <div class="text-caption mt-1" :style="{ opacity: headerTextOpacity }">
                  <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                  {{ tenureDisplay }}
                  <v-chip  v-if="customerSegment.priority"  x-small  class="ml-2">  {{ segmentPriorityLabel }}</v-chip>
                </div>
              </v-col>
               
              <v-spacer />

              <!-- Grade Chip -->
              <v-col cols="auto">
                <v-chip
                  big
                  outlined
                  elevation="3"
                  :style="chipStyle"
                >
                  <v-icon left size="22">mdi-shield-check</v-icon>
                  <span style="font-weight: bold;">{{ totalGrade }}</span>
                  <span style="opacity: 0.95;">({{ totalScore.toFixed(1) }})</span>
                </v-chip>
              </v-col>
            </v-row>
          </v-card-title>

          <v-divider />

          <!-- Main Score Card -->
          <v-card-text class="pt-4 pb-2 px-6">
            <v-row align="center" justify="center">
              <v-col cols="auto">
                <v-progress-circular
                  :rotate="-90"
                  :size="140"
                  :width="18"
                  :value="totalScore"
                  :color="totalScoreColor"
                  class="elevation-0"
                >
                  <div class="text-center mt-1">
                    <div
                      class="text-h2 font-weight-bold"
                      :style="{ color: totalScoreColor }"
                    >
                      {{ totalGrade }}
                    </div>
                    <div class="text-caption text--secondary font-weight-medium">
                      Overall
                    </div>
                  </div>
                </v-progress-circular>
              </v-col>
              <v-col cols="auto" class="ml-6">
                <div class="text-subtitle-1 text--secondary mb-2">Total Score</div>
                <div class="text-h4 font-weight-bold" :style="{ color: totalScoreColor }">
                  {{ totalScore.toFixed(1) }} <span class="text-body-2 text--secondary">/ 100</span>
                </div>

                <!-- Category Grades Summary -->
                <div class="text-body-2 mt-3">
                  <v-chip
                    v-for="(cat, catKey) in categories"
                    :key="catKey"
                    :color="categoryColors[catKey].gradeChip"
                    label
                    small
                    :class="['mr-1', 'mb-1']"
                  >
                    <v-icon left x-small>{{ categoryColors[catKey].icon }}</v-icon>
                    {{ cat.grade }}
                  </v-chip>
                </div>

                <!-- Key Alerts -->
                <div v-if="hasCriticalAlerts" class="mt-3">
                  <v-alert
                    v-for="(alert, idx) in criticalAlerts"
                    :key="idx"
                    :type="alert.severity"
                    dense
                    border="left"
                    colored-border
                    class="ma-1"
                    elevation="1"
                  >
                    <v-icon size="14" class="mr-1">{{ alert.icon }}</v-icon>
                    <span class="text-caption">{{ alert.message }}</span>
                  </v-alert>
                </div>
              </v-col>
            </v-row>
            <v-row>
            <!-- Date Picker -->
                <v-col cols="12" sm="6" md="3" lg="3" class="mt-3">
                  <v-menu
                    ref="dateMenu"
                    v-model="datePickerOpen"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="selectedDateFormatted"
                        label="As Of Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        hide-details
                        class="date-picker-field"
                        :disabled="loadingBenchmark"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="selectedDate"
                      @input="onDateSelected"
                      :max="new Date().toISOString().substr(0, 10)"
                      scrollable
                      color="primary"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
            </v-row>
          </v-card-text>

          <!-- Key Metrics Grid -->
          <v-card-text class="pb-4 px-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
              <v-icon left color="primary">mdi-speedometer</v-icon>
              Key Metrics
            </div>

            <v-row dense>
              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.onTime.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.onTime.icon" size="28">mdi-check-circle</v-icon>
                  <div class="text-caption text--secondary mt-1">On-Time Rate</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ metrics.onTimePct }}%</div>
                  <v-chip v-if="onTimeTrend" x-small :color="onTimeTrend.badge_color" label class="mt-1">
                    {{ onTimeTrend.badge_label }}
                  </v-chip>
                </v-sheet>
              </v-col>

              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.avgDelay.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.avgDelay.icon" size="28">mdi-clock-outline</v-icon>
                  <div class="text-caption text--secondary mt-1">Avg Delay</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ metrics.avgDelayStr }}</div>
                  <div class="text-caption">days</div>
                </v-sheet>
              </v-col>

              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.tenure.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.tenure.icon" size="28">mdi-history</v-icon>
                  <div class="text-caption text--secondary mt-1">Tenure</div>
                  <div v-if="metrics.tenureYears>=1.0">
                    <div class="text-h6 font-weight-bold mt-1">{{ metrics.tenureYears }}</div>
                    <div class="text-caption">years</div>
                  </div>
                  <span v-else>
                    <div class="text-h6 font-weight-bold mt-1">{{ metrics.tenureDays }}</div>
                    <div class="text-caption">days</div>
                  </span>
                </v-sheet>
              </v-col>

              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.visits.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.visits.icon" size="28">mdi-calendar-sync</v-icon>
                  <div class="text-caption text--secondary mt-1">Visits/Month</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ metrics.visitsPerMonth }}</div>
                  <div class="text-caption">avg</div>
                </v-sheet>
              </v-col>

              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.revenue.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.revenue.icon" size="28">mdi-cash-register</v-icon>
                  <div class="text-caption text--secondary mt-1">Lifetime Revenue</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ metrics.lifetimeRevenue }}</div>
                  <div class="text-caption">EUR</div>
                </v-sheet>
              </v-col>

              <v-col cols="6" sm="4" lg="2">
                <v-sheet
                  :color="metricTiles.outstanding.bg"
                  rounded="lg"
                  class="pa-3 metric-tile elevation-1 hover-card"
                  height="100%"
                >
                  <v-icon :color="metricTiles.outstanding.icon" size="28">mdi-file-document-alert-outline</v-icon>
                  <div class="text-caption text--secondary mt-1">Outstanding</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ metrics.outstandingEur }}</div>
                  <div class="text-caption">EUR</div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Trend Badges Section -->
          <v-card-text v-if="hasTrendBadges" class="pb-4 px-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
              <v-icon left color="info">mdi-trending-up</v-icon>
              Trends & Insights
            </div>

            <v-row dense>
              <!-- Punctuality Trend -->
              <v-col cols="12" sm="6" md="3">
                <v-sheet
                  rounded="lg"
                  :color="getBadgeColor(punctualityTrend)"
                  class="pa-3 trend-badge elevation-1"
                >
                  <div class="text-caption text--secondary">Punctuality</div>
                  <div class="text-h6 font-weight-bold mt-1">
                    {{ punctualityTrend.interpretation.badge_label || 'N/A' }}
                  </div>
                  <v-chip
                    v-if="punctualityTrend"
                    x-small
                    :color="punctualityTrend.interpretation.badge_color"
                    label
                    class="mt-1"
                  >
                    {{ punctualityTrend.direction > 0 ? '↑ Improving' : '↓ Declining' }}
                  </v-chip>
                  <div class="text-caption mt-2" v-html="punctualityTrend.short_description || ''"></div>
                </v-sheet>
              </v-col>

              <!-- Revenue Trend -->
              <v-col cols="12" sm="6" md="3">
                <v-sheet
                  rounded="lg"
                  :color="getBadgeColor(revenueTrend)"
                  class="pa-3 trend-badge elevation-1"
                >
                  <div class="text-caption text--secondary">Revenue</div>
                  <div class="text-h6 font-weight-bold mt-1">
                    {{ revenueTrend.interpretation.badge_label || 'N/A' }}
                  </div>
                  <v-chip
                    v-if="revenueTrend"
                    x-small
                    :color="revenueTrend.interpretation.badge_color"
                    label
                    class="mt-1"
                  >
                    {{ revenueTrend.direction > 0 ? '↑ Up' : '↓ Down' }}
                  </v-chip>
                  <div class="text-caption mt-2" v-html="revenueTrend.short_description || ''"></div>
                </v-sheet>
              </v-col>

              <!-- Profit Trend -->
              <v-col cols="12" sm="6" md="3">
                <v-sheet
                  rounded="lg"
                  :color="getBadgeColor(profitTrend)"
                  class="pa-3 trend-badge elevation-1"
                >
                  <div class="text-caption text--secondary">Profit</div>
                  <div class="text-h6 font-weight-bold mt-1">
                    {{ profitTrend.interpretation.badge_label || 'N/A' }}
                  </div>
                  <v-chip
                    v-if="profitTrend"
                    x-small
                    :color="profitTrend.interpretation.badge_color"
                    label
                    class="mt-1"
                  >
                    {{ profitTrend.direction > 0 ? '↑ Improving' : '↓ Worsening' }}
                  </v-chip>
                  <div class="text-caption mt-2" v-html="profitTrend.short_description || ''"></div>
                </v-sheet>
              </v-col>

              <!-- Fleet Penetration -->
              <v-col cols="12" sm="6" md="3">
                <v-sheet
                  rounded="lg"
                  color="blue lighten-5"
                  class="pa-3 trend-badge elevation-1"
                >
                  <div class="text-caption text--secondary">Fleet Penetration</div>
                  <div class="text-h6 font-weight-bold mt-1">{{ fleetPenetration }}%</div>
                  <div class="text-caption mt-1">of fleet using us</div>
                  <v-progress-linear
                    :value="fleetPenetration"
                    color="primary"
                    height="6"
                    rounded
                    class="mt-2"
                  />
                </v-sheet>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Credit Allowance Section -->
          <v-card-text v-if="hasCreditData && showCreditSection" class="pb-4 px-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
              <v-icon left color="success">mdi-wallet</v-icon>
              Credit Allowance
            </div>

            <v-sheet rounded="lg" :color="creditRecommendationColor + ' lighten-5'" class="pa-4 elevation-1">
              <v-row>
                <!-- Credit Limit Card -->
                <v-col cols="12" sm="6" md="3">
                  <v-sheet rounded="md" class="pa-3" color="white">
                    <div class="text-caption text--secondary">Credit Limit</div>
                    <div class="text-h5 font-weight-bold mt-1">
                      {{ creditLimitFormatted }}
                    </div>
                    <div class="text-caption mt-1">
                      <v-icon x-small :color="creditRecommendationColor">mdi-check-circle</v-icon>
                      {{ creditRecommendation }}
                    </div>
                  </v-sheet>
                </v-col>

                <!-- Available Credit Card -->
                <v-col cols="12" sm="6" md="3">
                  <v-sheet rounded="md" class="pa-3" color="white">
                    <div class="text-caption text--secondary">Available Credit</div>
                    <div class="text-h5 font-weight-bold mt-1" :color="creditRecommendationColor">
                      {{ availableCreditFormatted }}
                    </div>
                    <div class="text-caption mt-1">
                      Utilization: {{ utilizationRate }}%
                    </div>
                  </v-sheet>
                </v-col>

                <!-- Credit Health Score -->
                <v-col cols="12" sm="6" md="3">
                  <v-sheet rounded="md" class="pa-3" color="white">
                    <div class="text-caption text--secondary">Credit Health</div>
                    <div class="text-h5 font-weight-bold mt-1">
                      <v-progress-circular
                        :size="40"
                        :width="4"
                        :value="creditHealthScore"
                        :color="creditHealthScore >= 80 ? 'success' : (creditHealthScore >= 60 ? 'warning' : 'error')"
                        class="mr-2"
                      >
                        <span class="text-caption font-weight-bold">{{ creditHealthScore }}</span>
                      </v-progress-circular>
                    </div>
                    <div class="text-caption mt-1">out of 100</div>
                  </v-sheet>
                </v-col>

                <!-- Recommendation Badge -->
                <v-col cols="12" sm="6" md="3">
                  <v-sheet rounded="md" class="pa-3" color="white" height="100%">
                    <div class="text-caption text--secondary mb-2">Status</div>
                    <v-chip
                      :color="creditRecommendationColor"
                      :class="[creditRecommendationColor + '-text']"
                      label
                      small
                    >
                      <v-icon left size="12">mdi-shield-check</v-icon>
                      {{ creditRecommendation }}
                    </v-chip>
                    <div class="text-caption mt-3 font-weight-medium">
                      {{ creditAllowance.interpretation.short_description || '' }}
                    </div>
                  </v-sheet>
                </v-col>
              </v-row>

              <!-- Detailed Interpretation (Expandable) -->
              <v-expand-x-transition>
                <div v-show="showCreditDetails">
                  <v-divider class="my-3" />

                  <v-row dense>
                    <v-col cols="12" md="8">
                      <div class="text-caption font-weight-medium mb-2">
                        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                        Full Interpretation
                      </div>
                      <div class="text-body-2">
                        {{ creditAllowance.interpretation.full_interpretation || '' }}
                      </div>
                      <div class="text-body-2 mt-2">
                        <strong>Calculation:</strong> Monthly revenue multiplier × payment behavior × risk adjustment
                      </div>
                      <div class="text-caption mt-1">
                        Multiplier: <strong>{{ creditAllowance.interpretation.multiplier }}</strong>× |
                        Risk Adjustment: <strong>{{ creditAllowance.interpretation.risk_adjustment }}</strong>x
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="text-caption font-weight-medium mb-2">
                        <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                        Calculation Date
                      </div>
                      <div class="text-body-2">
                        {{ creditAllowance.calculation_date
                          ? new Date(creditAllowance.calculation_date).toLocaleDateString('el-GR')
                          : 'N/A' }}
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-x-transition>

              <v-btn
                v-if="creditAllowance.interpretation"
                text
                small
                color="primary"
                class="mt-2"
                @click="showCreditDetails = !showCreditDetails"
              >
                <v-icon left size="16">
                  {{ showCreditDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                {{ showCreditDetails ? 'Hide Details' : 'Show Details' }}
              </v-btn>
            </v-sheet>
          </v-card-text>

          <!-- New Combined Last Visit Section with Timeline -->
          <v-card-text v-if="hasLastVisitStats" class="pb-4 px-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">
              <v-icon left color="info">mdi-calendar-star</v-icon>
              Last Visit & Settlement Timeline
            </div>

            <v-sheet rounded="lg" class="pa-4 last-visit-card" :color="lastVisitCardColor">
              <!-- Summary Row -->
              <v-row align="center">
                <v-col cols="12" md="6">
                  <v-row>
                    <v-col cols="6">
                      <div class="text-caption text--secondary">Last Visit</div>
                      <div class="text-h6 font-weight-bold">{{ lastVisitStats.date }}</div>
                      <div class="text-caption" :color="lastVisitStats.daysAgoColor">
                        {{ lastVisitStats.daysAgoText }}
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="text-caption text--secondary">Repair Cost</div>
                      <div class="text-h6 font-weight-bold">€{{ lastVisitStats.amount }}</div>
                      <div class="text-caption">Profit: €{{ lastVisitStats.profit }}</div>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="6">
                  <v-row>
                    <v-col cols="6">
                      <div class="text-caption text--secondary">Status</div>
                      <v-chip :color="lastVisitStats.statusColor" label small class="mt-2">
                        <v-icon left size="12">{{ lastVisitStats.statusIcon }}</v-icon>
                        {{ lastVisitStats.statusText }}
                      </v-chip>
                    </v-col>
                    <v-col cols="6">
                      <div class="text-caption text--secondary">Vehicle</div>
                      <div class="text-h6 font-weight-bold">{{ lastVisitPlate }}</div>
                      <div class="text-caption">{{ lastVisitDaysToDue }}</div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <!-- Settlement Timeline Visualization -->
              <v-divider class="my-3" />

              <div class="text-caption font-weight-medium mb-2">
                <v-icon x-small class="mr-1">mdi-timeline</v-icon>
                Settlement Progress
              </div>

              <v-sheet rounded="md" class="pa-3 timeline-container mb-3" color="grey lighten-5">
                <!-- Progress Bar with Markers -->
                <v-row align="center" no-gutters>
                  <v-col cols="12">
                    <div class="d-flex align-center">
                      <v-progress-linear
                        :value="timelineProgress"
                        :color="timelineColor"
                        height="10"
                        rounded
                        stream
                        class="flex-grow-1 mr-2"
                      >
                        <!-- Payment Marker on Progress Bar -->
                        <template v-if="isFullyPaid && timelinePaymentPosition > 0">
                          <div
                            :style="{ left: `${timelinePaymentPosition}%`, position: 'absolute' }"
                            class="timeline-marker-full"
                          >
                            <v-icon size="20" color="white">mdi-check-circle</v-icon>
                          </div>
                        </template>

                        <!-- Partial Payment Marker -->
                        <template v-if="isPartiallyPaid">
                          <div
                            :style="{ left: `${partialPaymentPosition}%`, position: 'absolute' }"
                            class="timeline-marker-partial"
                          >
                            <v-icon size="16" color="warning">mdi-progress-check</v-icon>
                          </div>
                        </template>
                      </v-progress-linear>

                      <!-- Percentage Label -->
                      <div class="text-caption font-weight-bold ml-2" style="min-width: 50px;">
                        {{ Math.round(timelineProgress) }}%
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Timeline Milestones -->
                <v-row class="mt-2" dense>
                  <v-col
                    v-for="milestone in sortedMilestones"
                    :key="milestone.key"
                    cols="3"
                    class="text-center milestone-column"
                  >
                    <v-avatar :color="milestone.avatarColor" size="32" class="mb-1 elevation-1">
                      <v-icon dark size="18">{{ milestone.icon }}</v-icon>
                    </v-avatar>
                    <div class="text-caption font-weight-bold">{{ milestone.title }}</div>
                    <div class="text-caption text--secondary">{{ milestone.date }}</div>
                    <div class="text-caption" :color="milestone.labelColor">{{ milestone.label }}</div>
                  </v-col>
                </v-row>

                <!-- Payment Coverage Details -->
                <v-row v-if="hasPaymentCoverage" class="mt-2">
                  <v-col cols="12">
                    <v-divider class="my-2" />
                    <div class="d-flex justify-space-between align-center">
                      <div class="text-body-2">
                        <v-icon size="16" class="mr-1" color="success">mdi-currency-eur</v-icon>
                        Total: <strong>€{{ lastVisitTimeline.totalAmount }}</strong>
                      </div>
                      <div class="text-body-2">
                        <v-icon size="16" class="mr-1" :color="hasPayments ? 'success' : 'error'">
                          {{ hasPayments ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        Paid: <strong>€{{ lastVisitTimeline.paidAmount }}</strong>
                      </div>
                      <div v-if="isPartiallyPaid" class="text-body-2">
                        <v-icon size="16" class="mr-1" color="warning">mdi-minus-circle</v-icon>
                        Remaining: <strong>€{{ lastVisitTimeline.remainingAmount }}</strong>
                      </div>
                    </div>

                    <!-- Coverage Progress -->
                    <v-progress-linear
                      :value="coveragePercentage"
                      :color="coverageColor"
                      height="12"
                      rounded
                      class="mt-2"
                    >
                      <template v-slot="{ value }">
                        <strong>{{ Math.round(value) }}% coverage</strong>
                      </template>
                    </v-progress-linear>
                  </v-col>
                </v-row>

                <!-- Warning for Overdue Unpaid -->
                <v-alert
                  v-if="isUnpaid && lastVisitDaysLate > 0"
                  type="warning"
                  dense
                  border="left"
                  colored-border
                  class="mt-2"
                  elevation="0"
                >
                  <div class="text-caption">
                    <v-icon size="14" class="mr-1">mdi-clock-alert</v-icon>
                    Invoice unpaid for {{ lastVisitDaysLate }} days past due date
                  </div>
                </v-alert>

                <!-- Early Payment Badge -->
                <v-alert
                  v-if="isFullyPaid && lastVisitTimeline.settledEarly"
                  type="success"
                  dense
                  border="left"
                  colored-border
                  class="mt-2"
                  elevation="0"
                >
                  <div class="text-caption">
                    <v-icon size="14" class="mr-1">mdi-trophy-award</v-icon>
                    Paid {{ Math.abs(lastVisitDaysToSettle) }} days early! Excellent payment behavior.
                  </div>
                </v-alert>
              </v-sheet>

              <!-- Matched Invoices Table -->
              <v-row v-if="lastVisitInvoices && lastVisitInvoices.length > 0">
                <v-col cols="12">
                  <div class="text-caption font-weight-medium mb-2">
                    <v-icon x-small class="mr-1">mdi-file-document-multiple</v-icon>
                    Matched Invoices ({{ lastVisitInvoices.length }})
                  </div>
                  <v-data-table
                    :headers="invoiceHeaders"
                    :items="lastVisitInvoices"
                    hide-default-footer
                    density="compact"
                    class="elevation-0 small-table"
                  >
                    <template v-slot:item.paid_status="{ item }">
                      <v-chip
                        :color="item.paid_status === 'paid' ? 'success' : (item.paid_status === 'partially_paid' ? 'warning' : 'error')"
                        x-small
                        label
                      >
                        {{ item.paid_status }}
                      </v-chip>
                    </template>
                    <template v-slot:item.outstanding_amount="{ item }">
                      <span :class="{ 'text-error': item.outstanding_amount > 0 }">
                        €{{ item.outstanding_amount.toFixed(2) }}
                      </span>
                    </template>
                    <template v-slot:item.covered_amount="{ item }">
                      <span v-if="item.covered_amount > 0" class="text-success">
                        €{{ item.covered_amount.toFixed(2) }}
                      </span>
                      <span v-else>-</span>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-sheet>
          </v-card-text>

          <!-- Category Breakdown (Expandable) -->
          <v-card-text class="pb-2 px-6">
            <div class="text-subtitle-1 font-weight-medium mb-3">Category Breakdown</div>

            <v-expansion-panels accordion variant="accordion">
              <v-expansion-panel
                v-for="(cat, catKey) in categories"
                :key="catKey"
              >
                <v-expansion-panel-header>
                  <v-row align="center">
                    <v-col cols="auto">
                      <v-avatar :color="categoryColors[catKey].avatar" size="36">
                        <v-icon dark size="20">{{ categoryColors[catKey].icon }}</v-icon>
                      </v-avatar>
                    </v-col>
                    <v-col>
                      <div class="font-weight-medium">{{ cat.label }}</div>
                      <div class="text-caption text--secondary">
                        Weight: {{ (cat.weight * 100).toFixed(0) }}%
                      </div>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <v-chip
                        :color="categoryColors[catKey].chip"
                        label
                        small
                        outlined
                        class="mr-2"
                      >
                        {{ cat.score.toFixed(0) }} pts
                      </v-chip>
                      <v-chip
                        :color="categoryColors[catKey].gradeChip"
                        label
                        small
                      >
                        {{ cat.grade }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-divider class="my-2" />

                  <!-- Category Factors with Progress -->
                  <v-row dense class="mt-2">
                    <v-col
                      v-for="(factor, factorKey) in cat.factors"
                      :key="factorKey"
                      cols="12"
                    >
                      <v-sheet rounded="md" class="pa-3 factor-tile">
                        <v-row align="center">
                          <v-col cols="6">
                            <div class="text-body-2 font-weight-medium">
                              {{ formatFactorLabel(factorKey) }}
                            </div>
                          </v-col>
                          <v-col cols="4">
                            <v-progress-linear
                              :value="calculateFactorPercent(factor.points, factor.max)"
                              :color="getFactorColor(factor.points, factor.max)"
                              height="6"
                              rounded
                            >
                            </v-progress-linear>
                          </v-col>
                          <v-col cols="2" class="text-right">
                            <div class="text-body-2 font-weight-bold">
                              {{ Math.round(factor.points) }}<span class="text-caption">/{{ factor.max }}</span>
                            </div>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>
                  </v-row>

                  <!-- Quick Insights Chips -->
                  <v-sheet
                    v-if="getCategoryInsights(catKey).length > 0"
                    rounded="md"
                    class="pa-3 mt-3"
                    color="grey lighten-5"
                  >
                    <div class="text-caption font-weight-medium mb-2">
                      <v-icon x-small class="mr-1">mdi-lightbulb-on</v-icon>
                      Quick Insights:
                    </div>
                    <v-chip
                      v-for="(insight, idx) in getCategoryInsights(catKey)"
                      :key="idx"
                      :color="insight.color"
                      :class="[insight.color + '-text', 'mr-2', 'mb-2']"
                      label
                      small
                      outlined
                    >
                      {{ insight.text }}
                    </v-chip>
                  </v-sheet>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>

          <!-- Additional Detailed Metrics -->
          <v-card-text class="pb-2 px-6">
            <v-expand-x-transition>
              <v-card
                v-if="showDetailedMetrics && hasDetailedData"
                flat
                class="mx-6 mb-4"
              >
                <v-divider />
                <v-list two-line>
                  <v-subheader class="text-subtitle-1 font-weight-medium">
                    <v-icon left color="primary">mdi-chart-multiple</v-icon>
                    Detailed Analytics
                  </v-subheader>

                  <!-- Activity Metrics -->
                  <v-list-item v-if="activityMetrics" density="compact" class="py-2">
                    <v-list-item-content>
                      <v-list-item-title>Activity Overview</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ activityMetrics.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="text-right">
                      <div class="text-body-1 font-weight-bold">
                        {{ activityMetrics.repairs_per_year }}/yr
                      </div>
                      <div class="text-caption text--secondary">
                        Avg interval: {{ activityMetrics.avg_interval }} days
                      </div>
                    </v-list-item-action>
                  </v-list-item>

                  <!-- Consistency Badge -->
                  <v-list-item v-if="visitConsistency" density="compact" class="py-2">
                    <v-list-item-content>
                      <v-list-item-title>Visit Consistency</v-list-item-title>
                    {{ visitConsistency.full_interpretation }}
                   <v-list-item-subtitle>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip
                        :color="visitConsistency.badge_color"
                        :class="[visitConsistency.badge_color + '-text']"
                        label
                        small
                      >
                        {{ visitConsistency.badge_label }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>

                  <!-- Fleet Metrics -->
                  <v-list-item v-if="fleetSize > 0" density="compact" class="py-2">
                    <v-list-item-content>
                      <v-list-item-title>Fleet Composition</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ fleetSize }} vehicles • {{ activeVehicles }} active
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="text-right">
                      <div class="text-body-1 font-weight-bold">
                        {{ visitsPerVehicle }} visits/vehicle
                      </div>
                    </v-list-item-action>
                  </v-list-item>

                  <!-- Lifetime Profit -->
                  <v-list-item
                    v-if="lifetimeProfit"
                    density="compact"
                    class="py-2"
                  >
                    <v-list-item-content>
                      <v-list-item-title>Lifetime Profit</v-list-item-title>
                      <v-list-item-subtitle>Total profit from all repairs</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="font-weight-bold text-right">
                      €{{ lifetimeProfit }}
                      <div class="text-caption text--secondary">
                        Margin: {{ profitMargin }}%
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>

                <v-divider />

                <!-- Recommended Actions -->
                <v-card-actions v-if="recommendedActions.length > 0">
                  <v-col>
                    <div class="text-caption font-weight-medium mb-2">
                      <v-icon x-small class="mr-1">mdi-checkbox-marked-circle</v-icon>
                      Recommended Actions:
                    </div>
                    <v-chip
                      v-for="(action, idx) in recommendedActions"
                      :key="idx"
                      :color="action.color"
                      class="mr-2 mb-2"
                      small
                    >
                      <v-icon left x-small>{{ action.icon }}</v-icon>
                      {{ action.text }}
                    </v-chip>
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-expand-x-transition>

            <v-btn
              v-if="hasDetailedData"
              text
              color="primary"
              block
              @click="showDetailedMetrics = !showDetailedMetrics"
            >
              <v-icon left>
                {{ showDetailedMetrics ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
              {{ showDetailedMetrics ? 'Hide' : 'Show' }} Detailed Metrics
            </v-btn>
          </v-card-text>

          <!-- Footer Actions -->
          <v-card-actions class="px-6 py-3">
            <v-spacer />
            <v-btn
              text
              color="grey darken-1"
              @click="dialogVisible = false"
            >
              Close
            </v-btn>
            <v-btn
              color="secondary"
              :disabled="!hasData"
              @click="refreshData"
            >
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn
              color="info"
              :disabled="!hasData || loadingBenchmark"
              :loading="loadingBenchmark"
              @click="fetchBenchmark"
            >
              <v-icon left>mdi-compare</v-icon>
              {{ loadingBenchmark ? 'Συγκριση...' : 'Συγκριση ΚΑΔ' }}
            </v-btn>

            <v-btn
              color="primary"
              :disabled="!hasData"
              @click="$emit('export-health-report')"
            >
              <v-icon left>mdi-file-export</v-icon>
              Export PDF
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!hasData || exporting"
              :loading="exporting"
              @click="exportAsImage"
            >
              <v-icon left>mdi-camera</v-icon>
              {{ exporting ? 'Exporting...' : 'Export Image' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';

export default {
  name: 'HealthScoreDialog',

  props: {
    value: { type: Boolean, default: false },
    customerIdProp: { type: [String, Number], default: '' },
    buttonText: { type: String, default: 'View Health Score' },
    buttonColor: { type: String, default: 'primary' },
    buttonOutlined: { type: Boolean, default: false },
    buttonSmall: { type: Boolean, default: false },
  },


async mounted() {
  // Get customer ID from route or prop
  this.customer_id = this.$route.params.id || this.customerIdProp;
  
  if (this.customer_id) {
    console.log('Fetching customer');
    await this.refreshData();
  }
},

  data() {
    return {
      dialogVisible: false,
      exporting: false,
      showDetailedMetrics: false,
      showCreditDetails: false,
      datePickerOpen: false,
      selectedDate: new Date().toISOString().substr(0, 10),
      healthData: null,
      loadingHealthData: false,
      showBenchmark: false,
      benchmarkData: null,
      loadingBenchmark: false,
      invoiceHeaders: [
        { text: 'Invoice ID', value: 'inv_id' },
        { text: 'Amount', value: 'total_value' },
        { text: 'Status', value: 'paid_status' },
        { text: 'Outstanding', value: 'outstanding_amount' },
        { text: 'Due Date', value: 'due_date' },
      ],
      factorLabels: {
        // Payment Reliability
        on_time_rate: 'On-Time Payment Rate',
        early_payment: 'Early Payments',
        avg_lateness: 'Average Lateness',
        settle_speed: 'Settlement Speed',
        completion: 'Invoice Completion',
        punctuality_trend: 'Punctuality Trend',
        worst_case: 'Worst-Case Exposure',
        // Engagement & Loyalty
        visit_frequency: 'Visit Frequency',
        recency: 'Recency',
        activity_trend: 'Activity Trend',
        consistency: 'Visit Consistency',
        tenure: 'Tenure',
        // Financial Value
        total_revenue: 'Lifetime Revenue',
        avg_profit: 'Avg Profit/Repair',
        profit_margin: 'Profit Margin',
        profit_trend: 'Profit Trend',
        revenue_trend: 'Revenue Trend',
        // Risk & Standing
        penalty_score: 'Manual Classification',
        debt_exposure: 'Debt Exposure',
        payment_coverage: 'Payment Coverage',
        outstanding_bal: 'Outstanding Balance',
      },
    };
  },

  computed: {
    selectedDateFormatted() {
    if (!this.selectedDate) return '';
    const date = new Date(this.selectedDate);
    return date.toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' });
  },
  
  hasData() {
    return this.healthData && Object.keys(this.healthData).length > 0;
  },

    dialogBgClass() {
      return ''
      // const score = this.totalScore;
      // if (score >= 90) return 'gradient-success';
      // if (score >= 80) return 'gradient-green';
      // if (score >= 70) return 'gradient-primary';
      // if (score >= 60) return 'gradient-warning';
      // if (score >= 50) return 'gradient-orange';
      // if (score >= 40) return 'gradient-deep-orange';
      // return 'gradient-error';
    },
    customerName() {
      if (this.hasData) return this.healthData.customer_name;
      return '';
    },

    hasTrendBadges() {
      return this.punctualityTrend || this.revenueTrend || this.profitTrend;
    },

    hasDetailedData() {
      return this.activityMetrics || this.visitConsistency || this.fleetSize > 0;
    },

    // Segment Info
    customerSegment() {
      const eng = this.rawMetrics.engagement_loyalty;
      if (!eng || !eng.customer_segment) return null;

      const seg = eng.customer_segment.value;
      return {
        segment: seg.segment,
        segment_label: this.formatSegmentLabel(seg.segment),
        priority: seg.priority,
        fleet_size: seg.fleet_size,
        growth_potential: seg.growth_potential_pct
      };
    },

    segmentBadgeIcon() {
      const key = this.customerSegment.segment
      switch (key) {
        case 'sole_operator':
          return 'mdi-car'
        case 'small_fleet':
          return 'mdi-car-multiple'
        case 'mid_size_fleet':
          return 'mdi-truck'
        case 'large_fleet':
          return 'mdi-fleet'
        case 'churned':
          return 'mdi-alert-circle-outline'
        case 'growth_opportunity':
          return 'mdi-trending-up'
        default:
          return 'mdi-tag'
      }
    },

    segmentPriorityLabel() {
      const priority = this.customerSegment.priority
      switch (priority) {
        case 'win_back':
          return 'win back'
        case 'up_sell':
          return 'upsell'
        case 'enterprise_retention':
          return 'enterprise retention'
        case 'growth_focus':
          return 'growth focus'
        case 'retention_focus':
          return 'retention focus'
        case 'loyalty_reward':
          return 'loyalty reward'
        case 'monitor':
          return 'monitor'
        default:
          return ''
      }
    },


    segmentBadgeColor() {
      if (!this.customerSegment) return 'grey';
      const priority = this.customerSegment.priority;
      if (priority === 'high_value') return 'success';
      if (priority === 'retention_focus') return 'warning';
      if (priority === 'at_risk') return 'error';
      return 'primary';
    },

    // Critical Alerts
    hasCriticalAlerts() {
      return this.criticalAlerts.length > 0;
    },

    criticalAlerts() {
      const alerts = [];
      const rm = this.rawMetrics;

      // Check unpaid invoices
      if (rm.risk_standing.total_outstanding_count.value && rm.risk_standing.total_outstanding_amount.value > 0) {
        alerts.push({
          severity: 'warning',
          icon: 'mdi-alert-circle',
          message: `${rm.risk_standing.total_outstanding_count.value} invoice(s) outstanding`
        });
      }

      // Check declining trends
      if (this.revenueTrend && this.revenueTrend.direction < 0) {
        alerts.push({
          severity: 'warning',
          icon: 'mdi-trending-down',
          message: 'Revenue declining'
        });
      }

      if (this.profitTrend && this.profitTrend.direction < 0) {
        alerts.push({
          severity: 'warning',
          icon: 'mdi-chart-line',
          message: 'Profit margin pressure'
        });
      }

      // Check long unpaid days
      if (rm.largest_unpaid_days && rm.largest_unpaid_days.value > 30) {
        alerts.push({
          severity: 'error',
          icon: 'mdi-clock-alert',
          message: `${rm.largest_unpaid_days.value} days overdue`
        });
      }

      return alerts;
    },

    // Recommended Actions
    recommendedActions() {
      const actions = [];
      const rm = this.rawMetrics;

      if (this.revenueTrend.direction < 0) {
        actions.push({
          icon: 'mdi-email',
          color: 'warning',
          text: 'Schedule retention meeting'
        });
      }

      if (rm.risk_standing.total_outstanding_count.value > 0) {
        actions.push({
          icon: 'mdi-cash-check',
          color: 'info',
          text: 'Follow up on outstanding invoices'
        });
      }

      if (this.visitConsistency.consistency_level === 'low') {
        actions.push({
          icon: 'mdi-calendar',
          color: 'primary',
          text: 'Send engagement reminder'
        });
      }

      return actions;
    },

    // Tenure Display
    tenureDisplay() {
      const rm = this.rawMetrics;
      if (!rm.engagement_loyalty.tenure_years) return 'New customer';

      const years = rm.engagement_loyalty.tenure_years.value;
      if (years >= 1) return `${years.toFixed(1)} years`;
      if (rm.engagement_loyalty.tenure_days.value) return `${rm.engagement_loyalty.tenure_days.value} days`;
      return 'Recent';
    },

    // Raw Metrics Access (new nested structure)
    rawMetrics() {
      if (this.hasData) return this.healthData.metrics.metrics;
      return {};
    },

    // Categories
    categories() {
      return this.healthData.categories || {};
    },

    // Core Scores
    totalScore() {
      if (this.hasData) return this.healthData.total_score;
      return 0;
    },

    totalGrade() {
      if (this.hasData) return this.healthData.total_grade;
      return 'N/A';
    },

    // Trends
    punctualityTrend() {
      const pm = this.rawMetrics.payment_reliability;
      return pm.punctuality_trend.value;
    },

    revenueTrend() {
      const fm = this.rawMetrics.financial_value;
      return fm.revenue_trend.value;
    },

    profitTrend() {
      const fm = this.rawMetrics.financial_value;
      return fm.profit_trend.value;
    },

    // Activity Metrics
    activityMetrics() {
      const em = this.rawMetrics.engagement_loyalty;
      if (!em) return null;

      return {
        repairs_per_year: em.activity_per_year.value,
        avg_interval: em.avg_repair_interval_days.value,
        description: `${em.repairs_lifetime.value || 0} total repairs over ${this.tenureDisplay}`
      };
    },

    // Visit Consistency
    visitConsistency() {
      const em = this.rawMetrics.engagement_loyalty;
      return em.visit_consistency_cv.value.interpretation;
    },

    // Fleet Data
    fleetSize() {
      const em = this.rawMetrics.engagement_loyalty;
      return em.fleet_size.value || 0;
    },

    activeVehicles() {
      const em = this.rawMetrics.engagement_loyalty;
      return em.active_vehicle_count.value || 0;
    },

    visitsPerVehicle() {
      const em = this.rawMetrics.engagement_loyalty;
      return em.visits_per_vehicle.value.toFixed(1) || '-';
    },

    fleetPenetration() {
      const em = this.rawMetrics.engagement_loyalty;
      return em.fleet_penetration_pct.value.toFixed(1) || '0';
    },

    // Financial Metrics
    lifetimeProfit() {
      const fm = this.rawMetrics.financial_value;
      return fm.total_profit_lifetime.value.toFixed(0);
    },

    lifetimeRevenue() {
      const fm = this.rawMetrics.financial_value;
      return fm.total_revenue_lifetime.value.toLocaleString() || '0';
    },

    profitMargin() {
      const fm = this.rawMetrics.financial_value;
      const margin = fm.profit_margin.value;
      return margin ? (margin * 100).toFixed(1) : '0';
    },

    // Last Visit Data
    lastVisitStats() {
      const rm = this.rawMetrics;
      const lastVisit = rm.engagement_loyalty.last_visit.value;

      if (!lastVisit) {
        return {
          date: 'N/A',
          daysAgoText: '',
          amount: '0',
          profit: '0',
          statusText: 'No visits',
          statusColor: 'grey',
          statusIcon: 'mdi-help',
        };
      }

      const daysAgo = lastVisit.repair.days_ago || 0;
      const amount = (lastVisit.repair.cost || 0).toFixed(0);
      const profit = (lastVisit.repair.profit || 0).toFixed(2);
      const paymentSummary = lastVisit.payment_summary || {};
      const status = paymentSummary.status || 'unknown';

      // Days ago text
      let daysAgoText = `${daysAgo} days ago`;
      if (daysAgo === 0) daysAgoText = 'Today';
      else if (daysAgo === 1) daysAgoText = 'Yesterday';

      // Status
      let statusText = 'Unknown';
      let statusColor = 'grey';
      let statusIcon = 'mdi-help';

      if (status === 'paid') {
        statusText = paymentSummary.paid_on_time ? 'Paid On Time' : 'Paid Late';
        statusColor = paymentSummary.paid_on_time ? 'success' : 'warning';
        statusIcon = paymentSummary.paid_on_time ? 'mdi-check-circle' : 'mdi-clock-alert';
      } else if (status === 'partially_paid') {
        statusText = 'Partially Paid';
        statusColor = 'warning';
        statusIcon = 'mdi-progress-check';
      } else if (status === 'unpaid') {
        statusText = 'Unpaid';
        statusColor = 'error';
        statusIcon = 'mdi-alert-circle';
      }

      return {
        date: lastVisit.repair.date ? new Date(lastVisit.repair.date).toLocaleDateString('el-GR') : 'N/A',
        daysAgoText,
        amount,
        profit,
        statusText,
        statusColor,
        statusIcon,
      };
    },

    lastVisitPlate() {
      const lastVisit = this.rawMetrics.engagement_loyalty.last_visit.value;
      return lastVisit.repair.plate || 'N/A';
    },

    lastVisitDaysToDue() {
      const paymentSummary = this.rawMetrics.engagement_loyalty.last_visit.value.payment_summary || {};
      const daysLate = paymentSummary.days_late || 0;
      if (daysLate < 0) return `${Math.abs(daysLate)} days before due`;
      return `${daysLate} days late`;
    },

    lastVisitInvoices() {
      const lastVisit = this.rawMetrics.engagement_loyalty.last_visit.value;
      return lastVisit.matched_invoices || [];
    },

// Credit Allowance Data
creditAllowance() {
  const cm = this.rawMetrics.payment_reliability;
  return cm.credit_allowance.value;
},

hasCreditData() {
  return this.creditAllowance && this.creditAllowance.credit_limit;
},

creditLimitFormatted() {
  if (!this.creditAllowance) return '€0';
  return `€${this.creditAllowance.credit_limit.toLocaleString()}`;
},

availableCreditFormatted() {
  if (!this.creditAllowance) return '€0';
  return `€${this.creditAllowance.available_credit.toFixed(2)}`;
},

utilizationRate() {
  return this.creditAllowance.utilization_rate.toFixed(1) || '0';
},

creditRecommendation() {
  return this.creditAllowance.interpretation.badge_label || 'N/A';
},

creditRecommendationColor() {
  if (!this.creditAllowance) return 'grey';
  return this.creditAllowance.interpretation.badge_color || 'grey';
},

creditHealthScore() {
  return this.creditAllowance.interpretation.credit_health_score || 0;
},

// Show Credit Section (toggle visibility)
showCreditSection() {
  return true; // Set to false if you want to hide initially
},

// Timeline & Settlement Data
hasLastVisitStats() {
  const rm = this.rawMetrics;
  return rm.engagement_loyalty.last_visit !== null &&
         rm.engagement_loyalty.last_visit.value !== null;
},

lastVisitTimeline() {
  const rm = this.rawMetrics;
  const lastVisit = rm.engagement_loyalty.last_visit.value;
  const paymentSummary = lastVisit.payment_summary || {};

  const invDate = lastVisit.repair.date;
  const dueDate = paymentSummary.due_date || lastVisit.repair.date;
  const settledDate = paymentSummary.settlement_date;
  //const today = new Date();

  const daysFromInvToDue = this.daysBetween(invDate, dueDate);
  const daysFromDueToSettle = paymentSummary.days_to_settle || 0;
  const daysFromDueToToday = lastVisit.payment_summary?.days_late || 0;

  return {
    // Dates
    invoiceDate: invDate ? new Date(invDate).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' }) : 'N/A',
    dueDate: dueDate ? new Date(dueDate).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' }) : 'N/A',
    settlementDate: settledDate ? new Date(settledDate).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' }) : 'Pending',

    // Labels
    invoiceLabel: 'Created',
    daysToDueLabel: `${daysFromInvToDue} days`,
    todayLabel: paymentSummary.days_late > 0 ? `${paymentSummary.days_late} days overdue` : 'Current',
    settlementLabel: this.isFullyPaid ? (daysFromDueToSettle <= 0 ? `${Math.abs(daysFromDueToSettle)} days early` : `${daysFromDueToSettle} days late`) :
                     (this.isPartiallyPaid ? 'Partial' : 'Awaiting'),

    // Totals
    totalAmount: (paymentSummary.total_amount || lastVisit.totals?.total_amount || 0).toFixed(2),
    paidAmount: this.isFullyPaid ? (paymentSummary.total_amount || 0).toFixed(2) :
                (paymentSummary.total_net || 0).toFixed(2),
    remainingAmount: (lastVisit.totals.total_outstanding || paymentSummary.total_amount - (paymentSummary.total_net || 0)).toFixed(2),

    // Flags
    settledEarly: daysFromDueToSettle < 0,
    hasPastDue: daysFromDueToToday > 0,
  };
},

// Timeline Calculations
timelineProgress() {
  const rm = this.rawMetrics;
  const paymentSummary = rm.engagement_loyalty.last_visit.value.payment_summary || {};
  const daysToSettle = paymentSummary.days_to_settle;
  const daysLate = paymentSummary.days_late || 0;

  if (this.isFullyPaid && daysToSettle !== null) {
    // Payment completed - show percentage through timeline
    const totalSpan = Math.abs(daysToSettle) + 30; // 30 days buffer
    const progress = daysToSettle < 0 ? 50 : Math.min(50 + (daysToSettle / totalSpan) * 50, 100);
    return Math.max(progress, 10);
  }

  if (this.isUnpaid) {
    // Not paid yet - show progress to due date
    if (daysLate < 0) {
      // Before due date
      const progress = 50 + (daysLate / 30) * 25;
      return Math.max(progress, 10);
    } else {
      // After due date - extend past 50%
      return Math.min(50 + (daysLate / 60) * 50, 90);
    }
  }

  if (this.isPartiallyPaid) {
    return paymentSummary.days_to_settle ? 60 : 50;
  }

  return 30;
},

timelineColor() {
  if (this.isFullyPaid) return 'success';
  if (this.isPartiallyPaid) return 'warning';
  if (this.isUnpaid) return 'error';
  return 'grey';
},

timelinePaymentPosition() {
  const rm = this.rawMetrics;
  const daysToSettle = rm.engagement_loyalty.last_visit.value.payment_summary.days_late;
  console.log(daysToSettle)
  if (daysToSettle === null || daysToSettle === undefined) return 50;

  // Position: due date = 50%, earlier = less, later = more
  const basePosition = 50;
  const shift = (daysToSettle / 30) * 25;
  return Math.min(Math.max(basePosition + shift, 15), 85);
},

partialPaymentPosition() {
  return this.timelinePaymentPosition;
},

hasPaymentCoverage() {
  return this.isFullyPaid || this.isPartiallyPaid;
},

coveragePercentage() {
  const rm = this.rawMetrics;
  const lv =  rm.engagement_loyalty.last_visit.value
  const total = lv.totals.total_amount || 0;
  const paid = (lv.totals.total_amount - lv.totals.total_outstanding) || 0;

  if (total <= 0) return 0;
  return Math.min((paid / total) * 100, 100);
},

coverageColor() {
  if (this.isFullyPaid) return 'success';
  if (this.isPartiallyPaid) return 'warning';
  return 'error';
},

hasPayments() {
  return this.isFullyPaid || this.isPartiallyPaid;
},

sortedMilestones() {
  const rm = this.rawMetrics;
  const lastVisit = rm.engagement_loyalty.last_visit.value;
  const paymentSummary = lastVisit.payment_summary || {};

  const invDate = lastVisit.repair.date;
  const dueDate = paymentSummary.due_date || lastVisit.repair.date;
  const settledDate = paymentSummary.settlement_date;
  const today = new Date();

  const milestones = [
    {
      key: 'invoice',
      icon: 'mdi-receipt',
      avatarColor: this.milestoneColors.invoiceDate,
      title: 'Invoice',
      date: this.lastVisitTimeline.invoiceDate,
      sortDate: invDate ? new Date(invDate) : null,
      label: this.lastVisitTimeline.invoiceLabel,
      labelColor: this.timelineItemColor('invoice'),
    },
    {
      key: 'due',
      icon: 'mdi-calendar-clock',
      avatarColor: this.milestoneColors.dueDate,
      title: 'Due',
      date: this.lastVisitTimeline.dueDate,
      sortDate: dueDate ? new Date(dueDate) : null,
      label: this.lastVisitTimeline.daysToDueLabel,
      labelColor: this.daysToDueColor,
    },
    {
      key: 'today',
      icon: 'mdi-calendar-today',
      avatarColor: 'primary',
      title: 'Today',
      date: this.todayFormatted,
      sortDate: today,
      label: this.lastVisitTimeline.todayLabel,
      labelColor: this.todayColor,
    },
    {
      key: 'settlement',
      icon: this.settlementIcon,
      avatarColor: this.milestoneColors.settlement,
      title: this.isFullyPaid ? 'Paid' : (this.isPartiallyPaid ? 'Partial' : 'Pending'),
      date: this.lastVisitTimeline.settlementDate,
      // Unpaid settlements sort last (null → pushed to end)
      sortDate: settledDate ? new Date(settledDate) : null,
      label: this.lastVisitTimeline.settlementLabel,
      labelColor: this.settlementColor,
    },
  ];

  return milestones.sort((a, b) => {
    if (!a.sortDate) return 1;
    if (!b.sortDate) return -1;
    return a.sortDate - b.sortDate;
  });
},

milestoneColors() {
  const rm = this.rawMetrics;
  const paymentSummary = rm.engagement_loyalty.last_visit.value.payment_summary || {};

  return {
    invoiceDate: 'grey',
    dueDate: paymentSummary.days_late < 0 ? 'warning' : (paymentSummary.days_late > 0 ? 'error' : 'info'),
    settlement: this.isFullyPaid ? 'success' : (this.isPartiallyPaid ? 'warning' : 'grey'),
  };
},

todayFormatted() {
  return new Date().toLocaleDateString('el-GR', { day: 'numeric', month: 'short', year: 'numeric' });
},

daysToDueColor() {
  const rm = this.rawMetrics;
  const daysToSettle = rm.engagement_loyalty.last_visit.value.payment_summary?.days_to_settle;

  if (daysToSettle === null) return 'grey';
  if (daysToSettle < 0) return 'success';
  if (daysToSettle <= 7) return 'warning';
  return 'error';
},

settlementColor() {
  if (this.isFullyPaid && this.rawMetrics.engagement_loyalty.last_visit.value.payment_summary?.paid_on_time) return 'success';
  if (this.isFullyPaid) return 'warning';
  if (this.isPartiallyPaid) return 'warning';
  return 'grey';
},

todayColor() {
  const rm = this.rawMetrics;
  const daysLate = rm.engagement_loyalty.last_visit.value.payment_summary?.days_late || 0;

  if (this.isUnpaid && daysLate > 0) return 'error';
  if (this.isUnpaid) return 'warning';
  return 'primary';
},

settlementIcon() {
  if (this.isFullyPaid) return 'mdi-check-circle';
  if (this.isPartiallyPaid) return 'mdi-progress-check';
  return 'mdi-clock-outline';
},

isFullyPaid() {
  const rm = this.rawMetrics;
  if (!this.hasLastVisitStats) return false;
  const lv = rm.engagement_loyalty.last_visit.value;
  const ps = lv.payment_summary || {};

  // Check explicit status field first
  if (ps.paid_status === 'paid') return true;

  // Fallback: settlement_date exists and nothing outstanding
  if (ps.settlement_date) {
    const outstanding = lv.totals?.total_outstanding ?? ps.total_outstanding ?? 0;
    return outstanding <= 0;
  }

  return false;
},

isPartiallyPaid() {
  const rm = this.rawMetrics;
  if (!this.hasLastVisitStats) return false;
  const lv = rm.engagement_loyalty.last_visit.value;
  const ps = lv.payment_summary || {};

  // Explicit status
  if (ps.paid_status === 'partially_paid') return true;

  // Fallback: has some payments but still has outstanding balance
  const paid = ps.total_net ?? lv.totals?.total_net ?? 0;
  const outstanding = lv.totals?.total_outstanding ?? ps.total_outstanding ?? 0;
  return paid > 0 && outstanding > 0 && !this.isFullyPaid;
},

isUnpaid() {
  const rm = this.rawMetrics;
  if (!this.hasLastVisitStats) return false;
  const lv = rm.engagement_loyalty.last_visit.value;
  const ps = lv.payment_summary || {};

  // Explicit status
  if (ps.status === 'unpaid' || ps.status === 'open') return true;
  return false;
},

lastVisitDaysLate() {
  const rm = this.rawMetrics;
  if (!this.hasLastVisitStats) return 0;
  return rm.engagement_loyalty.last_visit.value.payment_summary?.days_late ?? 0;
},

lastVisitDaysToSettle() {
  const rm = this.rawMetrics;
  if (!this.hasLastVisitStats) return 0;
  return rm.engagement_loyalty.last_visit.value.payment_summary?.days_to_settle ?? 0;
},



    // Metric Tiles
    metrics() {
      const rm = this.rawMetrics;
      const onTime = rm.payment_reliability.payment_on_time_pct.value;
      const avgDelay = rm.payment_reliability.avg_days_after_due.value;
      const tenure = rm.engagement_loyalty.tenure_years.value;
      const visits = rm.engagement_loyalty.repairs_per_month.value;
      const outstanding = rm.risk_standing.total_outstanding_amount.value;

      return {
        onTimePct: onTime.toFixed(1) ?? '-',
        avgDelay: avgDelay ?? null,
        avgDelayStr: avgDelay === null ? '-' : avgDelay.toFixed(1),
        tenureYears: tenure ? tenure.toFixed(1) : '-',
        tenureDays: rm.engagement_loyalty.tenure_days.value ?? '-',
        visitsPerMonth: visits.toFixed(2) ?? '-',
        outstandingEur: outstanding.toFixed(0) ?? '0',
        lifetimeRevenue: this.lifetimeRevenue,
      };
    },

    metricTiles() {
      const m = this.metrics;
      return {
        onTime: {
          bg: this.getColorForValue(parseFloat(m.onTimePct), 80, 90, false),
          icon: parseFloat(m.onTimePct) >= 80 ? 'success' : 'warning',
        },
        avgDelay: {
          bg: this.getColorForValue(parseFloat(m.avgDelay || 0), 0, 7, true),
          icon: parseFloat(m.avgDelay || 0) <= 7 ? 'success' : 'warning',
        },
        tenure: { bg: 'purple lighten-5', icon: 'primary' },
        visits: { bg: 'blue lighten-5', icon: 'info' },
        revenue: { bg: 'green lighten-5', icon: 'success' },
        outstanding: {
          bg: this.getColorForValue(m.outstandingEur, 0, 1500, true),
          icon: m.outstandingEur <= 0 ? 'success' : m.outstandingEur <= 1000 ? 'warning' : 'error',
        },
      };
    },

    // Category Colors
    categoryColors() {
      return {
        payment_reliability: { avatar: 'primary', icon: 'mdi-currency-eur', chip: 'primary', gradeChip: 'success', label: 'Payment Reliability' },
        engagement_loyalty: { avatar: 'indigo', icon: 'mdi-account-group', chip: 'indigo', gradeChip: 'info', label: 'Engagement & Loyalty' },
        financial_value: { avatar: 'green', icon: 'mdi-trending-up', chip: 'success', gradeChip: 'success', label: 'Financial Value' },
        risk_standing: { avatar: 'amber', icon: 'mdi-shield-alert', chip: 'amber', gradeChip: 'warning', label: 'Risk & Standing' },
      };
    },

    // Header Styles
    headerStyle() {
      const score = this.totalScore;

      if (score >= 90) {
        return { background: 'linear-gradient(135deg, #1b5e20 0%, #0d2f13 100%)', color: '#ffffff' };
      } else if (score >= 80) {
        return { background: 'linear-gradient(135deg, #2e7d32 0%, #1a4d20 100%)', color: '#ffffff' };
      } else if (score >= 70) {
        return { background: 'linear-gradient(135deg, #1b5e20 0%, #0d3d15 100%)', color: '#ffffff' };
      } else if (score >= 60) {
        return { background: 'linear-gradient(135deg, #f57c00 0%, #bf360c 100%)', color: '#ffffff' };
      } else if (score >= 50) {
        return { background: 'linear-gradient(135deg, #e65100 0%, #b71c1c 100%)', color: '#ffffff' };
      } else if (score >= 40) {
        return { background: 'linear-gradient(135deg, #c62828 0%, #7f0000 100%)', color: '#ffffff' };
      } else {
        return { background: 'linear-gradient(135deg, #b71c1c 0%, #4a0000 100%)', color: '#ffffff' };
      }
    },

    headerTextStyle() {
      return { textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' };
    },

    headerTextOpacity() {
      return 1;
      // const score = this.totalScore;
      // if (score >= 60 && score < 70) return 0.75;
      // if (score >= 50 && score < 60) return 0.70;
      // return 0.85;
    },

    chipStyle() {
      const score = this.totalScore;
      const isDarkBackground = score <0;

      if (isDarkBackground) {
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: score >= 60 ? '#f57c00' : (score >= 50 ? '#e65100' : '#c62828'),
          borderColor: 'rgba(255, 255, 255, 0.5)',
          fontWeight: 'bold',
        };
      } else {
        return {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          color: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.5)',
        };
      }
    },

    overallAvatarColor() {
      const score = this.totalScore;
      if (score >= 90) return 'success darken-3';
      if (score >= 80) return 'green darken-2';
      if (score >= 70) return 'green';
      if (score >= 60) return 'warning';
      if (score >= 50) return 'orange';
      if (score >= 40) return 'deep-orange';
      return 'error';
    },

    overallGradeIcon() {
      const score = this.totalScore;
      if (score >= 90) return 'mdi-star-circle';
      if (score >= 80) return 'mdi-star';
      if (score >= 70) return 'mdi-thumb-up';
      if (score >= 60) return 'mdi-thumbs-up-down';
      if (score >= 50) return 'mdi-alert-circle';
      if (score >= 40) return 'mdi-emoticon-sad';
      return 'mdi-emoticon-dead';
    },

    totalScoreColor() {
      const score = this.totalScore;
      if (score >= 90) return '#2e7d32';
      if (score >= 80) return '#43a047';
      if (score >= 70) return '#66bb6a';
      if (score >= 60) return '#fdd835';
      if (score >= 50) return '#ffb300';
      if (score >= 40) return '#ffa726';
      return '#ef5350';
    },

    // Last Visit Card Color
    lastVisitCardColor() {
      const status = this.lastVisitStats.statusText;
      if (status === 'Paid On Time') return 'green lighten-5';
      if (status === 'Paid Late') return 'orange lighten-5';
      if (status === 'Partially Paid') return 'orange lighten-5';
      if (status === 'Unpaid') return 'red lighten-5';
      return 'grey lighten-5';
    },

    // On-Time Trend for badge
    onTimeTrend() {
      return this.punctualityTrend.interpretation;
    },
  },

  methods: {
    /*
    async fetchBenchmark() {
      this.loadingBenchmark = true;
      this.showBenchmark = false;

      try {
        const response = await this.$api.get(`/customers/peer-benchmarking/${this.customerId}`);
        this.benchmarkData = response.data;
        this.showBenchmark = true;
      } catch (error) {
        console.error("Failed to fetch benchmark:", error);
      } finally {
        this.loadingBenchmark = false;
      }
    },
    */

    daysBetween(date1, date2) {
      if (!date1 || !date2) return 0;
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
    },

    formatSegmentLabel(segment) {
      const labels = {
        'small_fleet': 'Small Fleet',
        'enterprise': 'Enterprise',
        'individual': 'Individual',
        'new_customer': 'New Customer',
      };
      return labels[segment] || segment;
    },

    formatFactorLabel(key) {
      return this.factorLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    getColor(percentile) {
      if (percentile >= 80) return 'success';
      if (percentile >= 50) return 'warning';
      return 'error';
    },

    timelineItemColor(type) {
      const rm = this.rawMetrics;
      const paymentSummary = rm.engagement_loyalty.last_visit.value.payment_summary || {};

      if (type === 'invoice') return 'grey';
      if (type === 'due') {
        if (paymentSummary.days_late < 0) return 'warning';
        if (paymentSummary.days_late > 0) return 'error';
        return 'info';
      }
      return 'primary';
    },
    getBadgeColor(trend) {
      if (!trend) return 'grey lighten-5';
      const severity = trend.interpretation.severity;
      if (severity === 'positive' || severity === 'excellent') return 'success lighten-5';
      if (severity === 'negative') return 'error lighten-5';
      if (severity === 'caution' || severity === 'good') return 'warning lighten-5';
      return 'grey lighten-5';
    },

    getColorForValue(val, goodAt, warningAt, lowerBetter = true) {
      if (lowerBetter) {
        if (val <= goodAt) return 'green lighten-5';
        if (val <= warningAt) return 'yellow lighten-4';
        return 'red lighten-4';
      } else {
        if (val >= warningAt) return 'green lighten-5';
        if (val >= goodAt) return 'yellow lighten-4';
        return 'red lighten-4';
      }
    },

    getCategoryInsights(categoryKey) {
      const cat = this.categories[categoryKey];
      if (!cat) return [];

      const insights = [];

      if (categoryKey === 'payment_reliability') {
        const onTime = cat.factors.on_time_rate;
        if (onTime && onTime.points / onTime.max >= 0.8) {
          insights.push({ text: 'Consistent payer ✓', color: 'success' });
        }
        const worst = cat.factors.worst_case;
        if (worst && worst.points / worst.max < 0.5) {
          insights.push({ text: 'Watch overdue', color: 'warning' });
        }
      }

      if (categoryKey === 'engagement_loyalty') {
        const freq = cat.factors.visit_frequency;
        if (freq && freq.points / freq.max >= 0.7) {
          insights.push({ text: 'Regular customer', color: 'success' });
        }
        const recency = cat.factors.recency;
        if (recency && recency.points / recency.max < 0.5) {
          insights.push({ text: 'May be disengaging', color: 'warning' });
        }
      }

      if (categoryKey === 'financial_value') {
        const profit = cat.factors.avg_profit;
        const margin = cat.factors.profit_margin;

        if (profit && profit.points / profit.max >= 0.7) {
          insights.push({ text: 'High-value repairs', color: 'success' });
        }
        if (margin && margin.points / margin.max >= 0.8) {
          insights.push({ text: 'Strong margins', color: 'success' });
        }
      }

      if (categoryKey === 'risk_standing') {
        const debt = cat.factors.debt_exposure;
        const balance = cat.factors.outstanding_bal;

        if (debt && debt.points / debt.max < 0.6) {
          insights.push({ text: 'Debt concerns', color: 'warning' });
        }
        if (balance && balance.points / balance.max < 0.5) {
          insights.push({ text: 'Balance action needed', color: 'error' });
        }
      }

      return insights.length > 0 ? insights : [{ text: 'No issues detected', color: 'success' }];
    },

    calculateFactorPercent(points, max) {
      return Math.min(100, (points / max) * 100);
    },

    getFactorColor(points, max) {
      const pct = this.calculateFactorPercent(points, max);
      if (pct >= 80) return 'success';
      if (pct >= 60) return 'warning';
      return 'error';
    },
  async refreshData() {
    if (!this.customer_id) return;
    
    this.loadingHealthData = true;
    try {
      this.healthData = await this.fetchHealthScore(this.customer_id, this.selectedDate);
    } catch (error) {
      console.error("Failed to fetch health score:", error);
      this.$emit('error', { message: 'Failed to load health data' });
    } finally {
      this.loadingHealthData = false;
    }
  },

  async fetchHealthScore(id, as_of_date = null) {
    const params = {};
    if (as_of_date) {
      params.as_of_date = as_of_date;
    }
    
    const response = await this.$api.get(
      `/metrics/customers/${id}/health/full-health-report`,
      { params }
    );
    return response.data;
  },

  onDateSelected() {
    this.datePickerOpen = false;
    this.refreshData();
  },

  async fetchBenchmark() {
    if (!this.customer_id) return;
    
    this.loadingBenchmark = true;
    this.showBenchmark = false;

    try {
      const response = await this.$api.get(`/customers/peer-benchmarking/${this.customer_id}`);
      this.benchmarkData = response.data;
      this.showBenchmark = true;
    } catch (error) {
      console.error("Failed to fetch benchmark:", error);
    } finally {
      this.loadingBenchmark = false;
    }
  },

  async sendReminder() {
    if (!this.customer_id) return;
    await this.$api.get("/customers/debits/balance-reminder/" + this.customer_id);
  },


    /**
     * Export health dialog as PNG image
     */
    async exportAsImage() {
      if (!this.$refs.healthCard) {
        this.$emit('export-error', { message: 'Dialog element not found' });
        return;
      }

      this.exporting = true;

      try {
        const canvas = await html2canvas(this.$refs.healthCard, {
          scale: 1,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        canvas.toBlob((blob) => {
          if (!blob) throw new Error('Failed to create image blob');

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const safeCustomerId = String(this.customerId).replace(/[^a-z0-9]/gi, '_');
          const dateStr = new Date().toISOString().split('T')[0];

          link.download = `customer-health-${safeCustomerId}-${dateStr}.png`;
          link.href = url;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 100);

          this.$emit('export-success', { fileName: link.download });
        }, 'image/png');

      } catch (error) {
        console.error('Export failed:', error);
        this.$emit('export-error', { message: error.message });
      } finally {
        this.exporting = false;
      }
    },
  },
  
watch: {
  value(val) { 
    this.dialogVisible = val; 
    if (val && this.customer_id && !this.hasData) {
      this.refreshData();
    }
  },
  dialogVisible(val) { 
    this.$emit('input', val); 
  },
  customerIdProp(newVal) {
    if (newVal && newVal !== this.customer_id) {
      this.customer_id = newVal;
      this.refreshData();
    }
  },
},

};
</script>

<style scoped lang="scss">
.health-score-dialog {
  max-height: 90vh;
  overflow-y: auto;

  .hover-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    }
  }

  .trend-badge {
    min-height: 120px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .metric-tile {
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .small-table {
    font-size: 0.8rem;

    :deep(.v-data-table) {
      font-size: 0.8rem;
    }
  }

  .hover-segment-label {
    cursor: help;
  }
}

.hover-card, .metric-tile, .trend-badge {
  transition: all 0.2s ease;
}

@media (max-width: 600px) {
  .health-score-dialog {
    .v-row {
      margin-bottom: 8px !important;
    }
  }
}

.gradient-success {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.gradient-green {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%);
}

.gradient-primary {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.gradient-warning {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
}

.gradient-orange {
  background: linear-gradient(135deg, #fbe9e7 0%, #ffccbc 100%);
}

.gradient-deep-orange {
  background: linear-gradient(135deg, #fbe9e7 0%, #ffab91 100%);
}

.gradient-error {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
}

// Apply subtle background to card content areas
.v-card--gradient-success, .v-card--gradient-green, .v-card--gradient-primary,
.v-card--gradient-warning, .v-card--gradient-orange, .v-card--gradient-deep-orange,
.v-card--gradient-error {
  background-attachment: fixed;
}

.date-picker-field {
  max-width: 200px;
}

:deep(.v-menu__content) {
  padding: 8px;
}


</style>
